import { create } from "zustand";
import { rouletteApi, type RouletteSpinResult, type RouletteStatus } from "../api/roulette";
import { refreshHeroIfMountStatsChanged } from "./useMountsStore";

interface RouletteStore {
  status: RouletteStatus | null;
  loading: boolean;
  spinning: boolean;
  error: string | null;
  lastResult: RouletteSpinResult | null;

  refresh: () => Promise<void>;
  spin: () => Promise<RouletteSpinResult>;
  clearError: () => void;
  clearResult: () => void;
}

export const useRouletteStore = create<RouletteStore>((set, get) => ({
  status: null,
  loading: false,
  spinning: false,
  error: null,
  lastResult: null,

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const status = await rouletteApi.getStatus();
      set({ status });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load Fortune Wheel" });
    } finally {
      set({ loading: false });
    }
  },

  spin: async () => {
    if (get().spinning) {
      throw new Error("Spin already in progress");
    }
    set({ spinning: true, error: null, lastResult: null });
    try {
      const result = await rouletteApi.spin();
      set((s) => ({
        lastResult: result,
        status: s.status
          ? { ...s.status, cost_available: result.cost_available }
          : s.status,
      }));
      await refreshHeroIfMountStatsChanged({ rewardType: result.reward.type });
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Spin failed";
      set({ error: message });
      throw e;
    } finally {
      set({ spinning: false });
    }
  },

  clearError: () => set({ error: null }),
  clearResult: () => set({ lastResult: null }),
}));
